import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Download, FileText, Loader2, ShieldCheck, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  fr: {
    trigger: 'Télécharger le deck institutionnel',
    triggerShort: 'Deck investisseurs',
    title: 'Deck institutionnel PLEXTERRA',
    subtitle: 'Document de 12 pages conçu pour votre comité d\'investissement — méthodologie, cas d\'étude, financement MLI Select et cadre réglementaire.',
    name: 'Nom complet',
    namePh: 'ex. Jean Tremblay',
    email: 'Courriel professionnel',
    emailPh: 'jean@familyoffice.ca',
    organization: 'Organisation',
    organizationPh: 'ex. Tremblay Capital',
    role: 'Fonction',
    rolePh: 'ex. Directeur des investissements',
    projectType: 'Type de projet à l\'étude',
    projectTypePh: 'ex. Multilogement 50+ unités, densification urbaine…',
    market: 'Marché principal',
    language: 'Langue du deck',
    languageOptions: { en: 'English', fr: 'Français' },
    consent: 'J\'autorise PLEXTERRA à me contacter au sujet de ce projet et à conserver mes coordonnées dans le respect de sa politique de confidentialité.',
    submit: 'Télécharger le PDF (12 pages)',
    submitLoading: 'Génération du deck en cours…',
    success: 'Téléchargement lancé. Notre équipe vous contactera dans les 24 heures ouvrables.',
    error: 'Une erreur est survenue. Veuillez réessayer ou nous écrire à info@plexterra.co.',
    badge: 'CONFIDENTIEL · POUR COMITÉ D\'INVESTISSEMENT',
    close: 'Fermer',
    trust: '24 h de délai de réponse · Document nominatif',
  },
  en: {
    trigger: 'Download the institutional deck',
    triggerShort: 'Investor deck',
    title: 'PLEXTERRA Institutional Deck',
    subtitle: '12-page document designed for your investment committee — methodology, case studies, CMHC MLI Select financing and regulatory framework.',
    name: 'Full name',
    namePh: 'e.g. John Doe',
    email: 'Business email',
    emailPh: 'john@familyoffice.com',
    organization: 'Organization',
    organizationPh: 'e.g. Doe Family Office',
    role: 'Role',
    rolePh: 'e.g. Managing Partner',
    projectType: 'Project type under review',
    projectTypePh: 'e.g. Multifamily 50+ units, urban densification…',
    market: 'Primary market',
    language: 'Deck language',
    languageOptions: { en: 'English', fr: 'Français' },
    consent: 'I authorize PLEXTERRA to contact me about this project and to retain my details under its privacy policy.',
    submit: 'Download the PDF (12 pages)',
    submitLoading: 'Generating deck…',
    success: 'Download started. Our team will reach out within 24 business hours.',
    error: 'Something went wrong. Please retry or reach us at info@plexterra.co.',
    badge: 'CONFIDENTIAL · FOR INVESTMENT COMMITTEE',
    close: 'Close',
    trust: '24-hour response SLA · Personalized document',
  },
};

const InvestorDeckModal = ({ open, onOpenChange, triggerLabel }) => {
  const { language } = useLanguage();
  const t = COPY[language] || COPY.en;

  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    role: '',
    project_type: '',
    market: 'CA',
    language: language === 'fr' ? 'fr' : 'en',
    consent: false,
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const resp = await fetch(`${BACKEND_URL}/api/investor-deck/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `HTTP ${resp.status}`);
      }
      // Stream PDF blob → trigger browser download
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = form.name.replace(/\s+/g, '_');
      a.download = `PLEXTERRA_Institutional_Deck_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setStatus('success');
      // Analytics
      try {
        if (window.gtag) window.gtag('event', 'investor_deck_downloaded', { language: form.language, market: form.market });
      } catch { /* no-op */ }
    } catch (err) {
      setErrorMsg(err.message || 'error');
      setStatus('error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#F7F5EF] border-0 p-0 overflow-hidden"
        data-testid="investor-deck-modal"
      >
        {/* Navy header band */}
        <div className="bg-[#0B1E3F] px-6 py-5 text-white">
          <div className="flex items-center gap-2 text-[#C6A15A] text-[10px] tracking-[0.25em] font-medium mb-2">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t.badge}
          </div>
          <DialogHeader className="space-y-2">
            <DialogTitle className="font-serif text-2xl text-white leading-tight">{t.title}</DialogTitle>
            <DialogDescription className="text-white/70 text-sm leading-relaxed">
              {t.subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>

        {status === 'success' ? (
          <div className="px-6 py-10 text-center space-y-4" data-testid="deck-success-state">
            <div className="w-16 h-16 rounded-full bg-[#C6A15A]/10 mx-auto flex items-center justify-center">
              <FileText className="w-7 h-7 text-[#C6A15A]" strokeWidth={1.5} />
            </div>
            <p className="font-serif text-xl text-[#0B1E3F]">{t.success}</p>
            <p className="text-xs text-[#0B1E3F]/60">{t.trust}</p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-4 px-5 py-2 text-xs tracking-widest uppercase bg-[#0B1E3F] text-white hover:bg-[#0B1E3F]/90 transition"
              data-testid="deck-close-btn"
            >
              {t.close}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-4" data-testid="deck-form">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t.name}>
                <input
                  required minLength={2}
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  placeholder={t.namePh}
                  className="w-full px-3 py-2 bg-white border border-[#0B1E3F]/15 text-sm text-[#0B1E3F] focus:outline-none focus:border-[#C6A15A]"
                  data-testid="deck-input-name"
                />
              </Field>
              <Field label={t.email}>
                <input
                  required type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder={t.emailPh}
                  className="w-full px-3 py-2 bg-white border border-[#0B1E3F]/15 text-sm text-[#0B1E3F] focus:outline-none focus:border-[#C6A15A]"
                  data-testid="deck-input-email"
                />
              </Field>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t.organization}>
                <input
                  required minLength={2}
                  type="text"
                  value={form.organization}
                  onChange={update('organization')}
                  placeholder={t.organizationPh}
                  className="w-full px-3 py-2 bg-white border border-[#0B1E3F]/15 text-sm text-[#0B1E3F] focus:outline-none focus:border-[#C6A15A]"
                  data-testid="deck-input-organization"
                />
              </Field>
              <Field label={t.role}>
                <input
                  required minLength={2}
                  type="text"
                  value={form.role}
                  onChange={update('role')}
                  placeholder={t.rolePh}
                  className="w-full px-3 py-2 bg-white border border-[#0B1E3F]/15 text-sm text-[#0B1E3F] focus:outline-none focus:border-[#C6A15A]"
                  data-testid="deck-input-role"
                />
              </Field>
            </div>

            {/* Row 3 */}
            <Field label={t.projectType}>
              <input
                required minLength={2}
                type="text"
                value={form.project_type}
                onChange={update('project_type')}
                placeholder={t.projectTypePh}
                className="w-full px-3 py-2 bg-white border border-[#0B1E3F]/15 text-sm text-[#0B1E3F] focus:outline-none focus:border-[#C6A15A]"
                data-testid="deck-input-project-type"
              />
            </Field>

            {/* Row 4 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t.market}>
                <select
                  value={form.market}
                  onChange={update('market')}
                  className="w-full px-3 py-2 bg-white border border-[#0B1E3F]/15 text-sm text-[#0B1E3F] focus:outline-none focus:border-[#C6A15A]"
                  data-testid="deck-select-market"
                >
                  <option value="CA">Canada (CAD)</option>
                  <option value="US">United States (USD)</option>
                </select>
              </Field>
              <Field label={t.language}>
                <select
                  value={form.language}
                  onChange={update('language')}
                  className="w-full px-3 py-2 bg-white border border-[#0B1E3F]/15 text-sm text-[#0B1E3F] focus:outline-none focus:border-[#C6A15A]"
                  data-testid="deck-select-language"
                >
                  <option value="en">{t.languageOptions.en}</option>
                  <option value="fr">{t.languageOptions.fr}</option>
                </select>
              </Field>
            </div>

            {/* Consent */}
            <label className="flex items-start gap-3 pt-2 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={form.consent}
                onChange={update('consent')}
                className="mt-0.5 accent-[#C6A15A]"
                data-testid="deck-consent-checkbox"
              />
              <span className="text-xs text-[#0B1E3F]/70 leading-relaxed">{t.consent}</span>
            </label>

            {status === 'error' && (
              <p className="text-xs text-red-600" data-testid="deck-error-message">
                {t.error} {errorMsg && <span className="text-red-500/60">({errorMsg.slice(0, 80)})</span>}
              </p>
            )}

            <DialogFooter className="pt-2">
              <button
                type="submit"
                disabled={status === 'loading' || !form.consent}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0B1E3F] text-white text-xs tracking-[0.25em] uppercase font-medium hover:bg-[#0B1E3F]/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
                data-testid="deck-submit-btn"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    {t.submitLoading}
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" strokeWidth={1.5} />
                    {t.submit}
                  </>
                )}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.2em] uppercase text-[#0B1E3F]/60 font-medium mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

// Standalone trigger button that manages its own modal state.
// Use this when you just want a CTA that pops the modal.
export const InvestorDeckTrigger = ({ variant = 'primary', className = '', label, testId = 'investor-deck-trigger' }) => {
  const { language } = useLanguage();
  const t = COPY[language] || COPY.en;
  const [open, setOpen] = useState(false);
  const styles = variant === 'primary'
    ? 'bg-[#C6A15A] text-[#0B1E3F] hover:bg-[#B08F45]'
    : variant === 'ghost'
      ? 'bg-transparent text-[#0B1E3F] border border-[#0B1E3F]/20 hover:border-[#C6A15A]'
      : 'bg-[#0B1E3F] text-white hover:bg-[#0B1E3F]/90';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.25em] uppercase font-medium transition ${styles} ${className}`}
        data-testid={testId}
      >
        <FileText className="w-4 h-4" strokeWidth={1.5} />
        {label || t.trigger}
      </button>
      <InvestorDeckModal open={open} onOpenChange={setOpen} />
    </>
  );
};

export default InvestorDeckModal;
